import React, { useState, useEffect } from 'react';
import { auth, db } from '../../../firebase';
import { addDoc, collection, serverTimestamp, updateDoc, doc, arrayUnion, getDocs, query, where } from 'firebase/firestore';
import ImageUploader from '../../ImageUploader';
import FormInfoTip from '../../ui/FormInfoTip';
import MainEventerTimingFields from '../../roster/MainEventerTimingFields';
import { rosterFormInfo } from '../../../utils/rosterFormInfo';
import {
  MAIN_EVENTER_COOLDOWN_DAYS,
  MAIN_EVENTER_DURATION_DAYS,
  clampMainEventerTimingDays,
} from '../../../utils/mainEventerCooldown';

const CreateRosterForm = ({ onBack, onComplete, ownerId }) => {
  const [rosterData, setRosterData] = useState({
    name: '',
    maxWrestlers: 6,
    maxCredits: 300,
    dataInizioPunteggio: '',
    owner: '',
    imageUrl: '',
    description: '',
    gameType: 'Competitivo',
    minMaleWrestlers: 0,
    minFemaleWrestlers: 0,
    onlyMainRoster: false,
    onlyNXT: false,
    // NUOVI CAMPI per allineamento
    isGM: false,
    hasBackstage: false,
    backstageCount: 0,
    hasMainEventer: true,
    mainEventerDurationDays: MAIN_EVENTER_DURATION_DAYS,
    mainEventerCooldownDays: MAIN_EVENTER_COOLDOWN_DAYS,
    addParticipantsNow: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [participantEmailInput, setParticipantEmailInput] = useState('');
  const [participantEmails, setParticipantEmails] = useState([]);
  const [participantInviteSummary, setParticipantInviteSummary] = useState({ added: [], pending: [] });
  const [participantEmailError, setParticipantEmailError] = useState('');

  const MIN_WRESTLERS = 6;
  const MAX_WRESTLERS = 20;
  const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);
  const participantEmailErrors = [
    'Inserisci un indirizzo email valido',
    'L\'email inserita non Ã¨ valida',
    'L\'email inserita non è valida',
    'Questa email Ã¨ giÃ  presente nella lista',
    'Questa email è già presente nella lista',
    'Nessun utente registrato trovato con questa email',
    'Impossibile verificare l\'email in questo momento'
  ];
  const inlineParticipantEmailError = participantEmailError || (participantEmailErrors.includes(error) ? error : '');

  // Carica utente corrente per default owner
  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setRosterData(prev => ({
        ...prev,
        owner: user.displayName || user.email.split('@')[0] || 'Giocatore',
      }));
    }
  }, []);

  /* ---------------- VALIDAZIONE FORM ---------------- */
  useEffect(() => {
    const backstageValid =
      !rosterData.hasBackstage ||
      (Number.isInteger(rosterData.backstageCount) &&
        rosterData.backstageCount > 0 &&
        rosterData.backstageCount <= Math.max(rosterData.maxWrestlers - (rosterData.hasMainEventer ? 1 : 0), 0));

    const isValid =
      rosterData.name.trim().length >= 3 &&
      rosterData.maxWrestlers >= MIN_WRESTLERS &&
      rosterData.maxWrestlers <= MAX_WRESTLERS &&
      rosterData.maxCredits > 0 &&
      rosterData.dataInizioPunteggio !== '' &&
      rosterData.minMaleWrestlers + rosterData.minFemaleWrestlers <= rosterData.maxWrestlers &&
      rosterData.owner.trim().length > 0 &&
      backstageValid;

    setIsFormValid(isValid);
  }, [rosterData]);

  /* --------- AGGIORNA CREDITI CONSIGLIATI --------- */
  useEffect(() => {
    setRosterData(prev => ({
      ...prev,
      maxCredits: prev.maxWrestlers * 50
    }));
  }, [rosterData.maxWrestlers]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue;

    if (type === 'checkbox') {
      newValue = checked;
    } else if (type === 'number' || type === 'range') {
      newValue = Number(value);
    } else {
      newValue = value;
    }

    // Auto‑esclusione per i filtri
    if (name === 'onlyMainRoster' && newValue === true) {
      setRosterData(prev => ({
        ...prev,
        onlyMainRoster: true,
        onlyNXT: false
      }));
      return;
    }

    if (name === 'onlyNXT' && newValue === true) {
      setRosterData(prev => ({
        ...prev,
        onlyNXT: true,
        onlyMainRoster: false
      }));
      return;
    }

    setRosterData(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (name === 'addParticipantsNow' && newValue === false) {
      setParticipantEmails([]);
      setParticipantEmailInput('');
      setParticipantEmailError('');
    }

    if (error) setError('');
  };

  const getTodayDate = () => new Date().toISOString().split('T')[0];

  const addParticipantEmail = async () => {
    const normalizedEmail = participantEmailInput.trim().toLowerCase();

    if (!normalizedEmail) {
      setParticipantEmailError('Inserisci un indirizzo email valido');
      return;
    }

    if (!isValidEmail(normalizedEmail)) {
      setError('L\'email inserita non è valida');
      return;
    }

    if (participantEmails.includes(normalizedEmail)) {
      setError('Questa email è già presente nella lista');
      return;
    }

    try {
      const usersQuery = query(collection(db, 'users'), where('email', '==', normalizedEmail));
      const usersSnap = await getDocs(usersQuery);

      if (usersSnap.empty) {
        setParticipantEmailError('Nessun utente registrato trovato con questa email');
        return;
      }
    } catch (err) {
      console.error('Errore durante il controllo email partecipante:', err);
      setParticipantEmailError('Impossibile verificare l\'email in questo momento');
      return;
    }

    setParticipantEmails(prev => [...prev, normalizedEmail]);
    setParticipantEmailInput('');
    setParticipantEmailError('');
  };

  const removeParticipantEmail = (emailToRemove) => {
    setParticipantEmails(prev => prev.filter(email => email !== emailToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setJoinCode('');
    setParticipantInviteSummary({ added: [], pending: [] });

    if (!isFormValid) {
      setError('Compila correttamente tutti i campi obbligatori');
      return;
    }

    if (rosterData.minMaleWrestlers + rosterData.minFemaleWrestlers > rosterData.maxWrestlers) {
      setError('La somma dei wrestler minimi non può superare il totale massimo');
      return;
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Utente non autenticato');

      const dataInizioTimestamp = new Date(rosterData.dataInizioPunteggio);
      const normalizedParticipantEmails = participantEmails
        .map(email => email.trim().toLowerCase())
        .filter(Boolean);
      const uniqueParticipantEmails = [...new Set(normalizedParticipantEmails)];
      const matchedParticipantIds = [];
      const pendingParticipantEmails = [];

      if (rosterData.addParticipantsNow && uniqueParticipantEmails.length > 0) {
        const userQueries = await Promise.all(
          uniqueParticipantEmails.map(async (email) => {
            const usersQuery = query(collection(db, 'users'), where('email', '==', email));
            const usersSnap = await getDocs(usersQuery);
            return { email, usersSnap };
          })
        );

        userQueries.forEach(({ email, usersSnap }) => {
          if (usersSnap.empty) {
            pendingParticipantEmails.push(email);
            return;
          }

          const invitedUserDoc = usersSnap.docs[0];
          if (invitedUserDoc.id !== user.uid) {
            matchedParticipantIds.push(invitedUserDoc.id);
          }
        });
      }

      const uniqueParticipantIds = [...new Set([user.uid, ...matchedParticipantIds])];

      const rosterToSave = {
        name: rosterData.name,
        maxWrestlers: rosterData.maxWrestlers,
        maxCredits: rosterData.maxCredits,
        participantIds: uniqueParticipantIds,
        ownerUid: user.uid,
        imageUrl: rosterData.imageUrl,
        description: rosterData.description,
        gameType: rosterData.gameType,
        dataInizioPunteggio: dataInizioTimestamp,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        minMaleWrestlers: rosterData.minMaleWrestlers,
        minFemaleWrestlers: rosterData.minFemaleWrestlers,
        onlyMainRoster: rosterData.onlyMainRoster,
        onlyNXT: rosterData.onlyNXT,
        private: true,
        // NUOVI CAMPI
        isGM: rosterData.isGM,
        hasBackstage: rosterData.hasBackstage,
        backstageCount: rosterData.hasBackstage ? rosterData.backstageCount : 0,
        hasMainEventer: rosterData.hasMainEventer,
        mainEventerDurationDays: clampMainEventerTimingDays(
          rosterData.mainEventerDurationDays,
          MAIN_EVENTER_DURATION_DAYS
        ),
        mainEventerCooldownDays: clampMainEventerTimingDays(
          rosterData.mainEventerCooldownDays,
          MAIN_EVENTER_COOLDOWN_DAYS
        ),
        participantInviteEmails: rosterData.addParticipantsNow ? uniqueParticipantEmails : [],
        pendingParticipantEmails: rosterData.addParticipantsNow ? pendingParticipantEmails : [],
      };

      const docRef = await addDoc(collection(db, 'rosters'), rosterToSave);
      const generatedCode = `${window.location.origin}/invite/${docRef.id}`;

      // Aggiorna l'utente con il roster creato
      await updateDoc(doc(db, 'users', user.uid), {
        ownerOfRosters: arrayUnion(docRef.id),
        rosterIds: arrayUnion(docRef.id)
      });

      if (matchedParticipantIds.length > 0) {
        await Promise.all(
          [...new Set(matchedParticipantIds)].map((participantId) =>
            updateDoc(doc(db, 'users', participantId), {
              rosterIds: arrayUnion(docRef.id)
            })
          )
        );
      }

      setJoinCode(generatedCode);
      setParticipantInviteSummary({
        added: uniqueParticipantEmails.filter(email => !pendingParticipantEmails.includes(email)),
        pending: pendingParticipantEmails
      });
      setSuccess(`Roster "${rosterData.name}" creato con successo!`);
    } catch (err) {
      console.error('Errore nella creazione del roster:', err);
      setError('Errore nella creazione del roster. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const copyJoinCode = async () => {
    if (!joinCode) return;
    try {
      await navigator.clipboard.writeText(joinCode);
      alert('Codice copiato! Condividilo con i giocatori per unirsi.');
    } catch (err) {
      console.error('Errore nella copia:', err);
      alert('Impossibile copiare il codice');
    }
  };

  return (
    <div className="bg-brand-dark">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8">
            <div>

              {/* Messaggi di successo/errore */}
              {success && !joinCode && (
                <div className="alert alert-success d-flex align-items-center gap-2 mb-4 border-0 rounded-3" style={{ background: 'rgba(46,204,113,0.1)', color: '#2ecc71' }}>
                  <i className="bi bi-check-circle-fill fs-5"></i>
                  <span>{success}</span>
                  <button type="button" className="btn-close ms-auto" onClick={() => setSuccess('')}></button>
                </div>
              )}

              {error && !participantEmailErrors.includes(error) && (
                <div className="alert alert-danger d-flex align-items-center gap-2 mb-4 border-0 rounded-3" style={{ background: 'rgba(231,76,60,0.1)', color: '#e74c3c' }}>
                  <i className="bi bi-exclamation-triangle-fill fs-5"></i>
                  <span>{error}</span>
                  <button type="button" className="btn-close ms-auto" onClick={() => setError('')}></button>
                </div>
              )}

              {/* Stato: Roster creato con codice */}
              {joinCode ? (
                <div className="text-center py-3">
                  <div className="mb-4">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center p-3 mb-3" style={{ background: 'rgba(46,204,113,0.15)' }}>
                      <i className="bi bi-check-lg fs-1" style={{ color: '#2ecc71' }}></i>
                    </div>
                    <h5 className="fw-bold mb-2" style={{ color: 'var(--brand-secondary)' }}>Roster creato con successo!</h5>
                  </div>

                  <div className="card bg-transparent border rounded-3 p-3 mb-4" style={{ borderColor: 'rgba(52,152,219,0.3)' }}>
                    <label className="fw-semibold mb-2" style={{ color: 'var(--brand-secondary)' }}>
                      <i className="bi bi-key me-1"></i> Link di invito
                    </label>
                    <div className="d-flex gap-2">
                      <code className="flex-grow-1 px-3 py-2 rounded bg-dark text-light" style={{ fontSize: '1rem', letterSpacing: '1px' }}>
                        {joinCode}
                      </code>
                      <button className="btn px-3" onClick={copyJoinCode} style={{ background: 'var(--brand-secondary)', color: 'white' }}>
                        <i className="bi bi-clipboard"></i>
                      </button>
                    </div>
                    <p className="text-brand-muted small mt-3 mb-0">
                      Condividi questo link con i giocatori che vuoi invitare.
                    </p>
                  </div>

                  {rosterData.addParticipantsNow && participantInviteSummary && (
                    <div className="bg-dark rounded-3 p-3 mb-4 text-start">
                      <h6 className="fw-semibold mb-2" style={{ color: 'var(--brand-secondary)' }}>
                        <i className="bi bi-envelope-paper me-1"></i> Inviti inviati
                      </h6>
                      {participantInviteSummary.added?.length > 0 && (
                        <p className="small mb-1 text-success">
                          ✅ {participantInviteSummary.added.length} partecipante(i) aggiunto(i)
                        </p>
                      )}
                      {participantInviteSummary.pending?.length > 0 && (
                        <p className="small mb-0 text-warning">
                          ⏳ {participantInviteSummary.pending.length} invito(i) in attesa
                        </p>
                      )}
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn-brand-secondary roster-form-btn roster-form-btn--primary mt-2"
                    onClick={onComplete}
                  >
                    <i className="bi bi-check-lg me-2"></i> Completa
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>

                  {/* Nome Roster */}
                  <div className="mb-4">
                    <div className="form-info-row--wrap mb-2">
                      <label className="form-label fw-semibold mb-0 d-flex align-items-center gap-2 text-brand-secondary">
                        <i className="bi bi-tag"></i>
                        <span className='text-light'>Nome Roster</span>
                        <span className="text-danger small">*</span>
                      </label>
                      <FormInfoTip title={rosterFormInfo.name.title}>
                        {rosterFormInfo.name.body}
                      </FormInfoTip>
                    </div>
                    <input
                      type="text"
                      className="form-control py-2"
                      name="name"
                      value={rosterData.name}
                      onChange={handleInputChange}
                      maxLength="30"
                      placeholder="Es: Ring of Champions"
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'white'
                      }}
                      required
                    />
                  </div>

                  {/* Configurazione rapida - Card compatta */}
                  <div className="bg-dark rounded-3 p-3 mb-4">
                    <div className="form-info-row--between mb-3 text-brand-secondary">
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-sliders2"></i>
                        <span className="fw-semibold text-light">Wrestler e crediti</span>
                      </div>
                      <FormInfoTip title={rosterFormInfo.slotsAndCredits.title}>
                        {rosterFormInfo.slotsAndCredits.body}
                      </FormInfoTip>
                    </div>

                    <div className="row g-3 ">
                      <div className="col-6 text-brand-secondary">
                        <label className="form-label small text-brand-muted mb-1">Wrestler per fazione</label>
                        <div className="d-flex align-items-center gap-2">
                          <i className="bi bi-people-fill"></i>
                          <input
                            type="range"
                            className="form-range flex-grow-1"
                            name="maxWrestlers"
                            min={MIN_WRESTLERS}
                            max={MAX_WRESTLERS}
                            step={1}
                            value={rosterData.maxWrestlers}
                            onChange={handleInputChange}
                          />
                          <span className="badge bg-brand-secondary text-dark rounded-pill">{rosterData.maxWrestlers}</span>
                        </div>
                      </div>
                      <div className="col-6">
                        <label className="form-label small text-brand-muted mb-1">Crediti</label>
                        <div className="d-flex align-items-center gap-2">
                          <i className="bi bi-currency-dollar text-brand-secondary"></i>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            name="maxCredits"
                            value={rosterData.maxCredits}
                            onChange={handleInputChange}
                            step="10"
                            style={{ background: 'rgba(255,255,255,0.05)', border: 'none', color: 'white' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Minimi di genere - 2 slider compatti */}
                  <div className="form-info-row mb-2">
                    <span className="small fw-semibold text-brand-secondary">Vincoli di composizione</span>
                    <FormInfoTip title={rosterFormInfo.composition.title}>
                      {rosterFormInfo.composition.body}
                    </FormInfoTip>
                  </div>
                  <div className="row g-3 mb-4">
                    <div className="col-6">
                      <label className="form-label small text-brand-muted mb-1">Min. Uomini</label>
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-gender-male" style={{ color: '#3498db' }}></i>
                        <input
                          type="range"
                          className="form-range flex-grow-1"
                          name="minMaleWrestlers"
                          min={0}
                          max={Math.floor(rosterData.maxWrestlers / 2)}
                          step={1}
                          value={rosterData.minMaleWrestlers}
                          onChange={handleInputChange}
                        />
                        <span className="badge bg-brand-secondary text-dark rounded-pill">{rosterData.minMaleWrestlers}</span>
                      </div>
                    </div>
                    <div className="col-6">
                      <label className="form-label small text-brand-muted mb-1">Min. Donne</label>
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-gender-female" style={{ color: '#e84393' }}></i>
                        <input
                          type="range"
                          className="form-range flex-grow-1"
                          name="minFemaleWrestlers"
                          min={0}
                          max={Math.floor(rosterData.maxWrestlers / 2)}
                          step={1}
                          value={rosterData.minFemaleWrestlers}
                          onChange={handleInputChange}
                        />
                        <span className="badge bg-brand-secondary text-dark rounded-pill">{rosterData.minFemaleWrestlers}</span>
                      </div>
                    </div>
                  </div>

                  {/* Warning minimi */}
                  {rosterData.minMaleWrestlers + rosterData.minFemaleWrestlers > rosterData.maxWrestlers && (
                    <div className="alert alert-warning py-2 mb-4 small" style={{ background: 'rgba(243,156,18,0.1)', border: 'none', color: '#f39c12' }}>
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      Somma minimi supera il max wrestler
                    </div>
                  )}

                  {/* Toggle opzioni - Switch compatti */}
                  <div className="bg-dark rounded-3 p-3 mb-4">
                    <div className="form-info-row--between mb-3">
                      <span className="small fw-semibold text-light">Filtri e ruoli speciali</span>
                      <FormInfoTip title={rosterFormInfo.options.title}>
                        {rosterFormInfo.options.body}
                      </FormInfoTip>
                    </div>
                    <div className="row g-2">
                      <div className="col-6">
                        <div className="d-flex align-items-center justify-content-between">
                          <label className="form-check-label small text-light" htmlFor="onlyMainRoster">Main Roster</label>
                          <div className="form-check form-switch m-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              name="onlyMainRoster"
                              id="onlyMainRoster"
                              checked={rosterData.onlyMainRoster}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="d-flex align-items-center justify-content-between">
                          <label className="form-check-label small text-light" htmlFor="onlyNXT">Solo NXT</label>
                          <div className="form-check form-switch m-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              name="onlyNXT"
                              id="onlyNXT"
                              checked={rosterData.onlyNXT}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="d-flex align-items-center justify-content-between">
                          <label className="form-check-label small text-light" htmlFor="isGM">Modalità GM</label>
                          <div className="form-check form-switch m-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              name="isGM"
                              id="isGM"
                              checked={rosterData.isGM}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="d-flex align-items-center justify-content-between">
                          <label className="form-check-label small text-light" htmlFor="hasBackstage">Backstage</label>
                          <div className="form-check form-switch m-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              name="hasBackstage"
                              id="hasBackstage"
                              checked={rosterData.hasBackstage}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="d-flex align-items-center justify-content-between">
                          <label className="form-check-label small text-light" htmlFor="hasMainEventer">Main Eventer</label>
                          <div className="form-check form-switch m-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              name="hasMainEventer"
                              id="hasMainEventer"
                              checked={rosterData.hasMainEventer}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <MainEventerTimingFields
                    enabled={rosterData.hasMainEventer}
                    durationDays={rosterData.mainEventerDurationDays}
                    cooldownDays={rosterData.mainEventerCooldownDays}
                    onDurationChange={(value) =>
                      setRosterData((prev) => ({
                        ...prev,
                        mainEventerDurationDays: value,
                      }))
                    }
                    onCooldownChange={(value) =>
                      setRosterData((prev) => ({
                        ...prev,
                        mainEventerCooldownDays: value,
                      }))
                    }
                    idPrefix="create-roster-main-eventer"
                  />

                  {/* Backstage count (condizionale) */}
                  {rosterData.hasBackstage && (
                    <div className="mb-4">
                      <div className="form-info-row mb-1">
                        <label className="form-label small text-brand-muted mb-0">Wrestler in Backstage</label>
                        <FormInfoTip title={rosterFormInfo.backstageCount.title}>
                          {rosterFormInfo.backstageCount.body}
                        </FormInfoTip>
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <i className="bi bi-door-closed text-brand-secondary"></i>
                        <input
                          type="range"
                          className="form-range flex-grow-1"
                          name="backstageCount"
                          min={1}
                          max={Math.max(rosterData.maxWrestlers - (rosterData.hasMainEventer ? 1 : 0), 1)}
                          step={1}
                          value={rosterData.backstageCount}
                          onChange={handleInputChange}
                        />
                        <span className="badge bg-brand-secondary text-dark rounded-pill">{rosterData.backstageCount}</span>
                      </div>
                    </div>
                  )}

                  {/* Data inizio */}
                  <div className="mb-4">
                    <div className="form-info-row--wrap mb-2">
                      <label className="form-label fw-semibold mb-0 d-flex align-items-center gap-2 text-brand-secondary">
                        <i className="bi bi-calendar-event"></i>
                        <span className='text-light'>Data inizio punteggio</span>
                        <span className="text-danger small">*</span>
                      </label>
                      <FormInfoTip title={rosterFormInfo.startDate.title}>
                        {rosterFormInfo.startDate.body}
                      </FormInfoTip>
                    </div>
                    <input
                      type="date"
                      className="form-control py-2"
                      name="dataInizioPunteggio"
                      min={getTodayDate()}
                      value={rosterData.dataInizioPunteggio}
                      onChange={handleInputChange}
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'white'
                      }}
                      required
                    />
                  </div>

                  {/* Sezione partecipanti - Versione compatta */}
                  <div className="bg-dark rounded-3 p-3 mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center gap-2 text-brand-secondary">
                        <i className="bi bi-envelope-paper"></i>
                        <span className="fw-semibold text-light">Inserisci partecipanti</span>
                        <FormInfoTip title={rosterFormInfo.participants.title}>
                          {rosterFormInfo.participants.body}
                        </FormInfoTip>
                      </div>
                      <div className="form-check form-switch m-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          name="addParticipantsNow"
                          id="addParticipantsNow"
                          checked={rosterData.addParticipantsNow}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {rosterData.addParticipantsNow ? (
                      <>
                        <div className="input-group input-group-sm mb-2">
                          <input
                            type="email"
                            className="form-control form-control-sm"
                            placeholder="email@esempio.com"
                            value={participantEmailInput}
                            onChange={(e) => {
                              setParticipantEmailInput(e.target.value);
                              if (participantEmailError) setParticipantEmailError('');
                            }}
                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'white' }}
                          />
                          <button className="btn btn-sm" type="button" onClick={addParticipantEmail} style={{ background: 'var(--brand-secondary)', color: 'white' }}>
                            <i className="bi bi-plus-lg"></i>
                          </button>
                        </div>
                        {inlineParticipantEmailError && (
                          <small className="text-danger d-block mb-2">{inlineParticipantEmailError}</small>
                        )}
                        <div className="d-flex flex-wrap gap-1">
                          {participantEmails.map((email) => (
                            <span key={email} className="badge d-inline-flex align-items-center gap-1 px-2 py-1" style={{ background: 'rgba(52,152,219,0.2)', color: '#3498db' }}>
                              {email}
                              <button type="button" className="btn-close btn-close-white ms-1" style={{ fontSize: '0.5rem' }} onClick={() => removeParticipantEmail(email)}></button>
                            </span>
                          ))}
                        </div>
                      </>
                    ) : (
                      <p className="small text-brand-muted mb-0">Puoi aggiungere i partecipanti successivamente dalla gestione roster.</p>
                    )}
                  </div>

                  {/* Descrizione - opzionale */}
                  <div className="mb-4">
                    <label className="form-label small text-brand-muted mb-1">Descrizione (opzionale)</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      name="description"
                      value={rosterData.description}
                      onChange={handleInputChange}
                      placeholder="Descrivi il tuo roster..."
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: '0.85rem' }}
                    />
                  </div>

                  <div className="bg-dark rounded-3 p-3 mb-4">
                    <div className="form-info-row--between mb-3 text-brand-secondary">
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-image"></i>
                        <span className="fw-semibold text-light">Immagine roster</span>
                      </div>
                      <FormInfoTip title={rosterFormInfo.image.title}>
                        {rosterFormInfo.image.body}
                      </FormInfoTip>
                    </div>
                    <ImageUploader
                      currentImageUrl={rosterData.imageUrl}
                      onUploadSuccess={(url) => setRosterData((prev) => ({ ...prev, imageUrl: url }))}
                      storagePath="rosters"
                      buttonText={rosterData.imageUrl ? 'Cambia immagine' : 'Carica immagine'}
                    />
                    {rosterData.imageUrl && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => setRosterData((prev) => ({ ...prev, imageUrl: '' }))}
                      >
                        <i className="bi bi-x-circle me-1"></i>
                        Rimuovi immagine
                      </button>
                    )}
                  </div>

                  {/* Pulsante crea */}
                  <button
                    type="submit"
                    className="btn btn-brand-secondary roster-form-btn roster-form-btn--primary roster-form-btn--block"
                    disabled={!isFormValid || loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Creazione in corso...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Crea Roster
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateRosterForm;
